import React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Button } from "@shopify/polaris";
import Modal from "components/Modal";
import { hideSessionExpiredModal } from "../../redux/features/auth/sessionExpiredSlice";
import { getLoginUrl } from "utils/environment";
import useAppDispatch from "hooks/useAppDispatch";
import { deleteAllCookies } from "utils/cookie";

const SessionExpiredModal = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const showModal = useSelector((state) => state.sessionExpired.showModal);

  // Never show session expired modal on activation or invitation routes
  const isActivationRoute = location.pathname.includes("/activation");
  const isInvitationRoute = location.pathname.startsWith("/join/accept") ||
    location.pathname.startsWith("/invitation") || 
    location.pathname.startsWith("/invite/accept") ||
    location.pathname.startsWith("/invite/profile");
  const shouldShowModal = showModal && !isActivationRoute && !isInvitationRoute;

  // Auto-hide modal state when on activation or invitation routes so it never appears and never redirects to login
  React.useEffect(() => {
    if ((isActivationRoute || isInvitationRoute) && showModal) {
      dispatch(hideSessionExpiredModal());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivationRoute, isInvitationRoute, showModal]);

  // Debug logging (only in development)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development' && showModal) {
    }
  }, [showModal]);

  const handleReload = () => {
    // Clear all localStorage
    try {
      localStorage.clear();
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }

    // Clear all sessionStorage
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error("Error clearing sessionStorage:", error);
    }

    // Clear all cookies
    try {
      deleteAllCookies();
    } catch (error) {
      console.error("Error clearing cookies:", error);
    }

    // Reset Redux store to initial state
    try {
      // Use global store reference to avoid import issues
      const store = typeof window !== 'undefined' ? window.__REDUX_STORE__ : null;
      if (store && typeof store.dispatch === 'function') {
        // Dispatch a reset action to clear all state
        store.dispatch({ type: 'RESET_STORE' });
      }
    } catch (error) {
      console.error("Error resetting Redux store:", error);
    }

    // Clear global store reference
    if (typeof window !== 'undefined' && window.__REDUX_STORE__) {
      try {
        delete window.__REDUX_STORE__;
      } catch (error) {
        console.error("Error clearing global store reference:", error);
      }
    }

    // Redirect to login page
    const loginUrl = getLoginUrl();
    window.location.href = loginUrl;
  };

  const handleClose = () => {
    dispatch(hideSessionExpiredModal());
  };

  return (
    <Modal
      visible={shouldShowModal}
      onClose={handleClose}
      title="Session Expired"
      description="Your session has expired. Please log in again."
      iconType="warning"
      closeIconVisible={false}
      shouldCloseOnOverlayClick={false}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="primary" onClick={handleReload}>
            Reload Page
          </Button>
        </div>
      }
    />
  );
};

export default SessionExpiredModal;

